import React, { useState, useEffect } from "react";
import { 
  Box, Typography, Paper, Chip, IconButton, Button, Container,
  Menu, MenuItem, ListItemIcon, ListItemText
} from "@mui/material";
import InventoryIcon from "@mui/icons-material/Inventory";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { alpha } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import { getPurchaseInvoices, deletePurchaseInvoice, updatePurchaseInvoicePaymentStatus } from "../services/purchaseService";
import { toast } from 'react-toastify';
import PaymentStatusDialog from '../../../components/PaymentStatusDialog';

function PurchaseOrdersListPage() {
  const [purchaseInvoices, setPurchaseInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [paymentDialog, setPaymentDialog] = useState({ open: false, invoice: null, isMarkingAsPaid: true });

  useEffect(() => {
    loadPurchaseInvoices();
  }, []);

  const loadPurchaseInvoices = async () => {
    try {
      setLoading(true);
      const data = await getPurchaseInvoices();
      const invoicesData = Array.isArray(data.payload) ? data.payload : Array.isArray(data) ? data : [];
      
      // ✅ Map backend data to ensure lowercase 'id' property
      const mappedInvoices = invoicesData.map(invoice => ({
        id: invoice.Id || invoice.id, // ✅ Handle both Id and id
        invoiceNumber: invoice.PurchaseInvoiceNumber || invoice.InvoiceNumber || invoice.invoiceNumber || 'N/A',
        supplierName: invoice.SupplierName || invoice.supplierName || 'Unknown Supplier',
        purchaseDate: invoice.InvoiceDate || invoice.PurchaseDate || invoice.purchaseDate,
        status: invoice.Status || invoice.status || 'Not Paid',
        subTotal: invoice.SubTotal || invoice.subTotal || 0,
        taxAmount: invoice.TaxAmount || invoice.taxAmount || 0,
        discountAmount: invoice.DiscountAmount || invoice.discountAmount || 0,
        totalAmount: invoice.TotalAmount || invoice.totalAmount || 0,
        items: invoice.LineItems || invoice.Items || invoice.items || [],
        referenceNumber: invoice.ReferenceNumber || invoice.referenceNumber,
        notes: invoice.Notes || invoice.notes,
        dueDate: invoice.DueDate || invoice.dueDate,
      }));
      
      setPurchaseInvoices(mappedInvoices);
    } catch (error) {
      console.error('Error loading purchase invoices:', error);
      toast.error('Failed to load purchase invoices');
      setPurchaseInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  const handleMenuOpen = (event, invoice) => {
    setAnchorEl(event.currentTarget);
    setSelectedInvoice(invoice);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedInvoice(null);
  };

  const handleOpenPaymentDialog = (invoice, isMarkingAsPaid) => {
    setPaymentDialog({ open: true, invoice, isMarkingAsPaid });
    handleMenuClose();
  };

  const handleClosePaymentDialog = () => {
    setPaymentDialog({ open: false, invoice: null, isMarkingAsPaid: true });
  };

  const handlePaymentSubmit = async (paymentForm) => {
    const { invoice, isMarkingAsPaid } = paymentDialog;
    if (!invoice) return;

    try {
      const statusDto = {
        PurchaseInvoiceId: invoice.id,
        Status: isMarkingAsPaid ? 'Paid' : 'Not Paid',
        PaidDate: isMarkingAsPaid ? paymentForm.paidDate : null,
        PaymentMethod: isMarkingAsPaid ? paymentForm.paymentSource : null,
        PaymentNotes: isMarkingAsPaid ? paymentForm.paymentNotes : null,
        Notes: null
      };

      await updatePurchaseInvoicePaymentStatus(statusDto);
      toast.success(
        isMarkingAsPaid
          ? 'Purchase invoice marked as paid successfully'
          : 'Purchase invoice marked as not paid successfully'
      );
      loadPurchaseInvoices();
    } catch (error) {
      console.error('Error updating payment status:', error);
      toast.error('Failed to update payment status');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this purchase invoice?')) {
      try {
        await deletePurchaseInvoice(id);
        toast.success('Purchase invoice deleted successfully');
        loadPurchaseInvoices();
      } catch (error) {
        console.error('Error deleting purchase invoice:', error);
        toast.error('Failed to delete purchase invoice');
      }
    }
    handleMenuClose();
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid':
        return 'success';
      case 'partial paid':
        return 'warning';
      case 'not paid':
      default:
        return 'error';
    }
  };

  const columns = [
    {
      field: "invoiceNumber",
      headerName: "Invoice Number",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="medium" color="primary">
          {params.value}
        </Typography>
      ),
    },
    {
      field: "supplierName",
      headerName: "Supplier",
      flex: 1.2,
      minWidth: 180,
    },
    {
      field: "purchaseDate",
      headerName: "Invoice Date",
      flex: 1,
      minWidth: 120,
      valueFormatter: (params) => {
        if (!params) return 'N/A';
        return new Date(params).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric'
        });
      },
    },
    {
      field: "dueDate",
      headerName: "Due Date",
      flex: 1,
      minWidth: 120,
      valueFormatter: (params) => {
        if (!params) return 'N/A';
        return new Date(params).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric'
        });
      },
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => (
        <Chip 
          label={params.value} 
          color={getStatusColor(params.value)} 
          size="small" 
          sx={{ fontWeight: 'bold' }}
        />
      ),
    },
    {
      field: "totalAmount",
      headerName: "Total Amount",
      flex: 1,
      minWidth: 130,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="bold" color="primary">
          €{params.value.toFixed(2)}
        </Typography>
      ),
    },
    {
      field: "itemsCount",
      headerName: "Items",
      flex: 0.6,
      minWidth: 80,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        
        <Chip 
          label={params.row.items?.length || 0} 
          color="info" 
          size="small" 
          sx={{ minWidth: '40px' }}
        />
      ),
      sortable: false,
      filterable: false,
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.5,
      minWidth: 70,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <IconButton
          onClick={(e) => handleMenuOpen(e, params.row)}
          sx={{ color: '#667eea' }}
          size="small"
        >
          <MoreVertIcon />
        </IconButton>
      ),
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
    },
  ];

  return (
    <Box sx={{ background: 'linear-gradient(to bottom, #f8fafc 0%, #f1f5f9 100%)', minHeight: '100vh', pt: 3, pb: 8 }}>
      <Container maxWidth="xl">
        <Paper elevation={3} sx={{ borderRadius: '16px', overflow: 'hidden', mb: 4, p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ bgcolor: alpha("#1976d2", 0.15), p: 1.2, borderRadius: '12px', mr: 2 }}>
                <InventoryIcon sx={{ color: "#1976d2" }} fontSize="large" />
              </Box>
              <Box>
                <Typography variant="h5" fontWeight="bold" color="primary">
                  Purchase Invoices
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Manage all your purchase orders and invoices
                </Typography>
              </Box>
              <Chip
                label={purchaseInvoices.length}
                color="primary"
                sx={{ ml: 2, fontWeight: 'bold', fontSize: '1em', borderRadius: '8px' }}
              />
            </Box>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              sx={{
                borderRadius: '12px',
                fontWeight: 'bold',
                textTransform: 'none',
                px: 3,
                py: 1
              }}
              onClick={() => navigate("/purchases/add")}
            >
              New Purchase Invoice
            </Button>
          </Box>
          
          <Box sx={{ height: 650, width: "100%" }}>
            <DataGrid
              rows={purchaseInvoices}
              columns={columns}
              pageSize={10}
              rowsPerPageOptions={[10, 25, 50, 100]}
              disableSelectionOnClick
              loading={loading}
              autoHeight={false}
              getRowId={(row) => row.id} // ✅ Explicitly set id getter
              sx={{
                borderRadius: 2,
                bgcolor: "#fff",
                fontSize: 14,
                "& .MuiDataGrid-columnHeaders": {
                  bgcolor: alpha("#1976d2", 0.08),
                  fontWeight: "bold",
                  fontSize: 15,
                },
                "& .MuiDataGrid-row": {
                  bgcolor: "#fff",
                  "&:hover": {
                    bgcolor: alpha("#1976d2", 0.03),
                  },
                },
                "& .MuiDataGrid-cell": {
                  borderColor: alpha("#1976d2", 0.1),
                },
                "& .MuiDataGrid-footerContainer": {
                  bgcolor: alpha("#1976d2", 0.03),
                },
              }}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 10, page: 0 },
                },
                sorting: {
                  sortModel: [{ field: 'purchaseDate', sort: 'desc' }],
                },
              }}
            />
          </Box>
        </Paper>

        {/* Context Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              minWidth: 200,
            },
          }}
        >
          <MenuItem
            onClick={() => {
              navigate(`/purchases/${selectedInvoice?.id}`);
              handleMenuClose();
            }}
          >
            <ListItemIcon>
              <VisibilityIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>View Details</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              navigate(`/purchases/edit/${selectedInvoice?.id}`);
              handleMenuClose();
            }}
          >
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleOpenPaymentDialog(selectedInvoice, selectedInvoice?.status?.toLowerCase() !== 'paid');
            }}
            sx={{
              color: selectedInvoice?.status?.toLowerCase() === 'paid' ? 'error.main' : 'success.main'
            }}
          >
            <ListItemIcon>
              {selectedInvoice?.status?.toLowerCase() === 'paid' ? (
                <CancelIcon fontSize="small" color="error" />
              ) : (
                <CheckCircleIcon fontSize="small" color="success" />
              )}
            </ListItemIcon>
            <ListItemText>
              {selectedInvoice?.status?.toLowerCase() === 'paid' ? 'Mark as Not Paid' : 'Mark as Paid'}
            </ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => handleDelete(selectedInvoice?.id)}
            sx={{ color: 'error.main' }}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        </Menu>

        {/* Payment Status Dialog */}
        <PaymentStatusDialog
          open={paymentDialog.open}
          onClose={handleClosePaymentDialog}
          onSubmit={handlePaymentSubmit}
          isMarkingAsPaid={paymentDialog.isMarkingAsPaid}
          document={{
            documentNo: paymentDialog.invoice?.invoiceNumber,
            SupplierName: paymentDialog.invoice?.supplierName,
            PurchaseInvoiceNumber: paymentDialog.invoice?.invoiceNumber,
          }}
          documentType="purchase-order"
        />
      </Container>
    </Box>
  );
}

export default PurchaseOrdersListPage;